import { useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { Link2, CheckCircle, Plus, Search, X } from "lucide-react"

/**
 * fortytools-Variante der HERO-Verknüpfungs-Karte in der Mail-Ansicht.
 * Wird bei backend_provider=fortytools STATT der HERO-Karte gerendert — die
 * HERO-Karte bleibt dadurch code-identisch (kein Regressionsrisiko).
 *
 * Zeigt: verknüpften Kunden + seine Objekte (keine HERO-Links/Popover), oder
 * bei erneuter Anfrage Bestandskunden per Absender-E-Mail (Bestandsabgleich)
 * + manuelle Kundensuche, oder die Anlege-Buttons (Kunde + Objekt / Nur Kunde).
 */
export type FortyObjekt = { id: number; name: string; street: string; zip: string; city: string; bunchofkeys: string }
export type FortyMatch = { customer_id: number; name: string; email: string; objekte: FortyObjekt[] }
export type FortyLinkInfo = {
  is_forty: boolean
  linked?: { customer_id: number; name: string; project_id?: number | null; objekte: FortyObjekt[] } | null
  matches?: FortyMatch[]
  /** Basis-URL zur fortytools-Weboberfläche (tenant-spezifisch), z.B.
   *  "https://app.fortytools.com/werkxpartner". Wenn gesetzt, werden Kunde +
   *  Objekte als Deep-Links gerendert. */
  forty_web_base?: string | null
}

function objektZeile(o: FortyObjekt): string {
  return [o.street, [o.zip, o.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")
}

function ObjektList({ items, webBase }: { items: FortyObjekt[]; webBase?: string | null }) {
  if (!items.length) return <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Noch keine Objekte</div>
  return (
    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map((o) => {
        const name = o.name || `Objekt #${o.id}`
        const href = webBase ? `${webBase}/facilities/${o.id}` : null
        return (
          <div key={o.id} style={{ fontSize: 12, padding: "4px 8px", background: "var(--secondary-bg)", borderRadius: 6 }}>
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-color)", fontWeight: 700 }}>{name}</a>
            ) : (
              <strong>{name}</strong>
            )}
            {objektZeile(o) && <span style={{ color: "var(--text-muted)" }}> · {objektZeile(o)}</span>}
            {o.bunchofkeys && <span style={{ color: "var(--text-muted)" }}> · 🔑 {o.bunchofkeys}</span>}
          </div>
        )
      })}
    </div>
  )
}

/** Kunden-Name als Deep-Link in fortytools (oder Plain-Text ohne web_base). */
function KundeName({ name, customerId, webBase, fallback }: { name?: string; customerId: number; webBase?: string | null; fallback: string }) {
  const label = name || fallback
  if (!webBase) return <strong>{label}</strong>
  return (
    <a href={`${webBase}/customers/${customerId}`} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-color)", fontWeight: 700 }}>
      {label}
    </a>
  )
}

function MatchRow({ m, onLink, linking, webBase }: { m: FortyMatch; onLink: (m: FortyMatch) => void; linking: boolean; webBase?: string | null }) {
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 8, marginBottom: 6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5 }}>
          <KundeName name={m.name} customerId={m.customer_id} webBase={webBase} fallback={`Kunde #${m.customer_id}`} />
        </span>
        <span style={{ color: "var(--text-muted)", fontSize: 12 }}>#{m.customer_id} · {m.objekte.length} Objekt(e)</span>
        <button className="ev2-hero-ibtn ev2-hero-ibtn-primary" style={{ marginLeft: "auto" }}
                onClick={() => onLink(m)} disabled={linking}>
          <Search className="w-3.5 h-3.5" /> Verknüpfen
        </button>
      </div>
      <ObjektList items={m.objekte} webBase={webBase} />
    </div>
  )
}

export function FortytoolsLinkCard({
  emailId, info, onRefetch, onCreateKundeObjekt, onCreateKunde, onCreateObjekt,
}: {
  emailId: number
  info?: FortyLinkInfo
  onRefetch: () => void
  onCreateKundeObjekt: () => void
  onCreateKunde: () => void
  onCreateObjekt: () => void
}) {
  const linked = info?.linked
  const matches = info?.matches || []
  const webBase = info?.forty_web_base

  const [q, setQ] = useState("")
  const [results, setResults] = useState<FortyMatch[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)

  const runSearch = async () => {
    if (q.trim().length < 2) return
    setSearching(true)
    try {
      const r = await fetch(`/api/v1/emails/${emailId}/forty-search?q=${encodeURIComponent(q.trim())}`, { credentials: "include" })
      const d = await r.json()
      setResults(d.results || [])
    } catch {
      setResults([])
    } finally {
      setSearching(false)
      setSearched(true)
    }
  }

  const patchLink = async (bodyObj: Record<string, unknown>) => {
    const r = await fetch(`/api/v1/emails/${emailId}/hero-link`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(bodyObj),
    })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
  }
  const link = useMutation({ mutationFn: (m: FortyMatch) => patchLink({ contact_id: m.customer_id, contact_name: m.name }), onSuccess: onRefetch })
  const unlink = useMutation({ mutationFn: () => patchLink({ contact_id: null, project_id: null }), onSuccess: onRefetch })

  return (
    <div className="ev2-hero-card">
      <div className="ev2-hero-card-title">
        <Link2 className="w-3.5 h-3.5" /> fortytools
      </div>
      <div className="ev2-hero-card-body">
        {linked ? (
          <>
            <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, flexWrap: "wrap" }}>
              <CheckCircle className="w-3.5 h-3.5" style={{ color: "#059669" }} />
              <KundeName name={linked.name} customerId={linked.customer_id} webBase={webBase} fallback="Kunde" />
              <span style={{ color: "var(--text-muted)", fontSize: 12 }}>Kunde #{linked.customer_id}</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>Objekte ({linked.objekte.length}):</div>
            <ObjektList items={linked.objekte} webBase={webBase} />
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
              <button className="ev2-hero-ibtn" onClick={onCreateObjekt} title="Weiteres Objekt für diesen Kunden anlegen">
                <Plus className="w-3.5 h-3.5" /> Objekt anlegen
              </button>
              <button className="ev2-hero-ibtn" onClick={() => unlink.mutate()} disabled={unlink.isPending} title="Verknüpfung zu diesem Kunden lösen">
                <X className="w-3.5 h-3.5" /> Verknüpfung lösen
              </button>
            </div>
          </>
        ) : (
          <>
            {matches.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 6 }}>
                  {matches.length === 1 ? "Bestehender fortytools-Kunde" : "Bestehende fortytools-Kunden"} mit dieser E-Mail — verknüpfen statt neu anlegen:
                </div>
                {matches.map((m) => (
                  <MatchRow key={m.customer_id} m={m} onLink={(x) => link.mutate(x)} linking={link.isPending} webBase={webBase} />
                ))}
              </div>
            )}

            {/* Manuelle Kundensuche (z.B. wenn Absender-Adresse abweicht) */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 6 }}>Bestehenden Kunden suchen:</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runSearch() }}
                  placeholder="Name / Kunde (min. 2 Zeichen)"
                  style={{ flex: 1, borderRadius: 6, border: "1px solid var(--border)", padding: "6px 10px", fontSize: 13, background: "var(--bg-input, #fff)" }}
                />
                <button className="ev2-hero-ibtn ev2-hero-ibtn-primary" onClick={runSearch} disabled={searching || q.trim().length < 2}>
                  <Search className="w-3.5 h-3.5" /> Suchen
                </button>
              </div>
              {searching && <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "4px 0" }}>Suche…</div>}
              {!searching && results.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  {results.map((m) => (
                    <MatchRow key={m.customer_id} m={m} onLink={(x) => link.mutate(x)} linking={link.isPending} webBase={webBase} />
                  ))}
                </div>
              )}
              {!searching && searched && results.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "4px 0" }}>Keine Kunden gefunden.</div>
              )}
            </div>

            {matches.length === 0 && !searched && (
              <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginBottom: 8 }}>Noch nicht verknüpft</div>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button className="ev2-hero-ibtn ev2-hero-ibtn-primary" onClick={onCreateKundeObjekt} title="Neuen Kunden + Objekt anlegen">
                <Plus className="w-3.5 h-3.5" /> Kunde + Objekt
              </button>
              <button className="ev2-hero-ibtn" onClick={onCreateKunde} title="Nur Kunden anlegen (ohne Objekt)">
                <Plus className="w-3.5 h-3.5" /> Nur Kunde
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
